import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, authApi, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, LogOut, ArrowLeft, Utensils, Download, UserX, UserPlus } from "lucide-react";
import ShiftsPanel from "@/components/admin/ShiftsPanel";
import ContentPanel from "@/components/admin/ContentPanel";
import ReportsPanel from "@/components/admin/ReportsPanel";

const TOKEN_KEY = "mowt_admin_token";

function LoginView({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/admin/login", { email, password });
      localStorage.setItem(TOKEN_KEY, data.token);
      onLogin(data.token);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#8E1226] text-[#FBF7F0] flex items-center justify-center px-6">
      <div className="w-full max-w-sm" data-testid="admin-login">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-[#FBF7F0]/60 hover:text-[#0E6B3A] transition-colors mb-10"
        >
          <ArrowLeft className="w-4 h-4" /> Back to site
        </Link>
        <div className="flex items-center gap-2.5 mb-8">
          <Utensils className="w-5 h-5 text-[#0E6B3A]" strokeWidth={1.6} />
          <span className="text-sm">Meals on Wheels Tralee</span>
        </div>
        <h1 className="font-serif font-light text-4xl mb-8">Admin sign-in</h1>
        <form onSubmit={submit} className="space-y-5">
          <div className="space-y-1.5">
            <Label htmlFor="email" className="text-[#FBF7F0]/80 text-sm">
              Email
            </Label>
            <Input
              id="email"
              type="email"
              data-testid="admin-email-input"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="bg-transparent border-0 border-b border-[#FBF7F0]/30 rounded-none px-0 text-[#FBF7F0] focus-visible:ring-0 focus-visible:border-[#0E6B3A]"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="password" className="text-[#FBF7F0]/80 text-sm">
              Password
            </Label>
            <Input
              id="password"
              type="password"
              data-testid="admin-password-input"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="bg-transparent border-0 border-b border-[#FBF7F0]/30 rounded-none px-0 text-[#FBF7F0] focus-visible:ring-0 focus-visible:border-[#0E6B3A]"
            />
          </div>
          <button
            type="submit"
            data-testid="admin-login-btn"
            disabled={loading}
            className="w-full rounded-full bg-[#0E6B3A] text-[#FBF7F0] py-3.5 text-sm font-medium tracking-wide hover:bg-[#083D21] transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
          >
            {loading && <Loader2 className="w-4 h-4 animate-spin" />}
            Sign in
          </button>
        </form>
      </div>
    </div>
  );
}

function RemoveSignupButton({ signup, token, onRemoved }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const remove = async () => {
    setBusy(true);
    try {
      await api.delete(`/admin/signups/${signup.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(`${signup.name} has been removed and emailed a cancellation.`);
      setOpen(false);
      onRemoved();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not remove this sign-up.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger asChild>
        <button
          data-testid={`signup-remove-${signup.id}`}
          className="inline-flex items-center gap-2 rounded-full bg-[#8E1226] text-[#FBF7F0] px-5 py-2.5 text-sm font-medium shadow-sm hover:bg-[#6B0D1C] transition-colors"
        >
          <UserX className="w-4 h-4" strokeWidth={1.8} />
          Remove
        </button>
      </AlertDialogTrigger>
      <AlertDialogContent className="bg-[#FBF7F0] border-[#8E1226]/10">
        <AlertDialogHeader>
          <AlertDialogTitle className="font-serif text-2xl text-[#8E1226]">
            Remove {signup.name}?
          </AlertDialogTitle>
          <AlertDialogDescription className="text-[#6D726A]">
            This takes <strong>{signup.name}</strong> off <strong>{signup.role}</strong> on{" "}
            <strong>{signup.day_label}, {signup.start} – {signup.end}</strong>. The spot opens up
            for someone else straight away, and we'll email {signup.name} to let them know their
            shift has been cancelled.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel className="rounded-full" data-testid={`signup-remove-${signup.id}-cancel`}>
            Keep them on
          </AlertDialogCancel>
          <AlertDialogAction
            data-testid={`signup-remove-${signup.id}-confirm`}
            disabled={busy}
            onClick={(e) => {
              e.preventDefault();
              remove();
            }}
            className="rounded-full bg-[#8E1226] hover:bg-[#6B0D1C] text-[#FBF7F0] px-6"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Yes, remove them"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

const EMPTY_VOLUNTEER = { name: "", phone: "", email: "", notes: "" };

function AddVolunteerDialog({ token, onAdded }) {
  const [open, setOpen] = useState(false);
  const [days, setDays] = useState(null);
  const [dayId, setDayId] = useState("");
  const [roleId, setRoleId] = useState("");
  const [slotId, setSlotId] = useState("");
  const [form, setForm] = useState(EMPTY_VOLUNTEER);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open && !days) {
      api
        .get("/shifts")
        .then(({ data }) => setDays(data.days || []))
        .catch(() => toast.error("Could not load the shift list."));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const reset = () => {
    setForm(EMPTY_VOLUNTEER);
    setDayId("");
    setRoleId("");
    setSlotId("");
  };

  const day = (days || []).find((d) => d.id === dayId);
  const role = (day?.roles || []).find((r) => r.id === roleId);
  const slots = role?.slots || [];
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (form.name.trim().length < 2) return toast.error("Please enter the volunteer's name.");
    if (form.phone.trim().length < 5) return toast.error("Please enter their phone number.");
    if (!slotId) return toast.error("Please pick a day, a job and a time.");
    setBusy(true);
    try {
      const payload = { name: form.name.trim(), phone: form.phone.trim(), shift_id: slotId, notes: form.notes.trim() };
      if (form.email.trim()) payload.email = form.email.trim();
      const { data } = await authApi(token).post("/admin/signups", payload);
      toast.success(
        payload.email
          ? `${data.name} is booked in — confirmation email sent.`
          : `${data.name} is booked in. (No email given, so nothing was sent.)`
      );
      setOpen(false);
      reset();
      onAdded();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not add this volunteer.");
    } finally {
      setBusy(false);
    }
  };

  const inputCls =
    "bg-transparent border-0 border-b border-[#8E1226]/20 rounded-none px-0 focus-visible:ring-0 focus-visible:border-[#0E6B3A]";

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button
          data-testid="add-volunteer-btn"
          className="inline-flex items-center gap-2 rounded-full bg-[#0E6B3A] text-[#FBF7F0] px-6 py-3 text-sm font-medium shadow-sm hover:bg-[#083D21] transition-colors"
        >
          <UserPlus className="w-4 h-4" strokeWidth={1.8} />
          Add a volunteer
        </button>
      </DialogTrigger>
      <DialogContent className="bg-[#FBF7F0] border-[#8E1226]/10 max-w-lg" data-testid="add-volunteer-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-3xl text-[#8E1226]">Add a volunteer</DialogTitle>
          <DialogDescription className="text-[#6D726A]">
            For people who phone or call in. Book them onto a shift here — if they have an email
            address we'll send them the usual confirmation.
          </DialogDescription>
        </DialogHeader>
        {!days ? (
          <div className="flex items-center gap-3 text-[#6D726A] py-8">
            <Loader2 className="w-5 h-5 animate-spin" /> Loading shifts…
          </div>
        ) : (
          <div className="space-y-5 pt-2">
            <div className="grid grid-cols-2 gap-5">
              <div className="space-y-1.5">
                <Label className="text-[#8E1226] text-sm">Name</Label>
                <Input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="add-volunteer-name" className={inputCls} placeholder="Mary O'Brien" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-[#8E1226] text-sm">Phone</Label>
                <Input value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="add-volunteer-phone" className={inputCls} placeholder="087 123 4567" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-[#8E1226] text-sm">Email (optional)</Label>
              <Input value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="add-volunteer-email" className={inputCls} placeholder="Only if they have one" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="space-y-1.5">
                <Label className="text-[#8E1226] text-sm">Day</Label>
                <Select value={dayId} onValueChange={(v) => { setDayId(v); setRoleId(""); setSlotId(""); }}>
                  <SelectTrigger data-testid="add-volunteer-day" className="border-[#8E1226]/20"><SelectValue placeholder="Pick a day" /></SelectTrigger>
                  <SelectContent>
                    {days.map((d) => (
                      <SelectItem key={d.id} value={d.id}>{d.label} · {d.date}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-[#8E1226] text-sm">Job</Label>
                <Select value={roleId} onValueChange={(v) => { setRoleId(v); setSlotId(""); }} disabled={!dayId}>
                  <SelectTrigger data-testid="add-volunteer-role" className="border-[#8E1226]/20"><SelectValue placeholder="Pick a job" /></SelectTrigger>
                  <SelectContent>
                    {(day?.roles || []).map((r) => (
                      <SelectItem key={r.id} value={r.id}>{r.role}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-[#8E1226] text-sm">Time</Label>
                <Select value={slotId} onValueChange={setSlotId} disabled={!roleId}>
                  <SelectTrigger data-testid="add-volunteer-slot" className="border-[#8E1226]/20"><SelectValue placeholder="Pick a time" /></SelectTrigger>
                  <SelectContent>
                    {slots.map((s) => (
                      <SelectItem key={s.id} value={s.id} disabled={s.remaining === 0}>
                        {s.start} – {s.end} · {s.remaining} of {s.capacity} left
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-[#8E1226] text-sm">Notes (optional)</Label>
              <Textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} data-testid="add-volunteer-notes" rows={2} className="bg-transparent border border-[#8E1226]/20 rounded-lg focus-visible:ring-0 focus-visible:border-[#0E6B3A] resize-none" placeholder="Anything we should know — allergies, experience, who referred them…" />
            </div>
            <button
              data-testid="add-volunteer-submit"
              onClick={submit}
              disabled={busy}
              className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#0E6B3A] text-[#FBF7F0] py-3.5 text-sm font-medium hover:bg-[#083D21] transition-colors disabled:opacity-60"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />}
              Book them in
            </button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Dashboard({ token, onLogout }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("signups");

  const load = async () => {
    try {
      const res = await api.get("/admin/signups", {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data);
    } catch (err) {
      if (err.response?.status === 401) {
        toast.error("Session expired. Please sign in again.");
        onLogout();
      } else {
        toast.error("Could not load sign-ups.");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading && tab === "signups") {
    // fall through; spinner shown inside the sign-ups tab
  }

  const totalCapacity = (data?.summary || []).reduce((a, s) => a + s.capacity, 0);
  const filled = (data?.summary || []).reduce((a, s) => a + s.taken, 0);

  const downloadCsv = () => {
    const rows = data?.signups || [];
    if (!rows.length) return;
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const headers = ["Name", "Email", "Phone", "Day", "Date", "Role", "Start", "End", "Notes", "Signed up (UTC)"];
    // Sort by Day then Role then Start so the file reads like a rota
    const sorted = [...rows].sort((a, b) =>
      (a.date + a.role + a.start).localeCompare(b.date + b.role + b.start)
    );
    const lines = [headers.map(esc).join(",")];
    sorted.forEach((s) => {
      lines.push(
        [s.name, s.email, s.phone, s.day_label, s.date, s.role, s.start, s.end, s.notes, s.created_at]
          .map(esc)
          .join(",")
      );
    });
    const blob = new Blob(["\uFEFF" + lines.join("\r\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `mowt-christmas-volunteers-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-[#FBF7F0]" data-testid="admin-dashboard">
      <header className="border-b border-[#8E1226]/10 sticky top-0 bg-[#FBF7F0]/90 backdrop-blur-xl z-10">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Utensils className="w-5 h-5 text-[#0E6B3A]" strokeWidth={1.6} />
            <span className="text-sm font-medium">Volunteer sign-ups</span>
          </div>
          <div className="flex items-center gap-6">
            <Link
              to="/"
              className="text-xs uppercase tracking-[0.2em] text-[#6D726A] hover:text-[#8E1226] transition-colors"
            >
              View site
            </Link>
            <button
              data-testid="admin-logout-btn"
              onClick={onLogout}
              className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#6D726A] hover:text-[#0E6B3A] transition-colors"
            >
              <LogOut className="w-4 h-4" /> Sign out
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-12 md:py-16">
        <h1 className="font-serif font-light text-5xl md:text-6xl tracking-tight text-[#8E1226]">
          Admin
        </h1>

        <div className="mt-8 flex flex-wrap gap-3 border-b border-[#8E1226]/10 pb-0">
          {[
            { id: "signups", label: "Sign-ups" },
            { id: "reports", label: "Reports" },
            { id: "shifts", label: "Shifts & Roles" },
            { id: "content", label: "Page Content" },
          ].map((t) => (
            <button
              key={t.id}
              data-testid={`admin-tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`relative px-1 pb-3 text-sm tracking-wide transition-colors ${
                tab === t.id ? "text-[#8E1226]" : "text-[#6D726A] hover:text-[#8E1226]"
              }`}
            >
              {t.label}
              {tab === t.id && (
                <span className="absolute left-0 right-0 -bottom-px h-0.5 bg-[#0E6B3A]" />
              )}
            </button>
          ))}
        </div>

        {tab === "signups" && (
          <div className="mt-10">
            {loading ? (
              <div className="flex items-center gap-3 text-[#6D726A] py-10">
                <Loader2 className="w-5 h-5 animate-spin" /> Loading sign-ups…
              </div>
            ) : (
              <>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {[
                    { label: "Total sign-ups", value: data?.total ?? 0 },
                    { label: "Spots filled", value: `${filled} / ${totalCapacity}` },
                    { label: "Roles offered", value: new Set((data?.summary || []).map((s) => s.role)).size },
                    { label: "Slots", value: (data?.summary || []).length },
                  ].map((c) => (
                    <div key={c.label} className="rounded-2xl border border-[#8E1226]/12 p-6">
                      <div className="text-3xl font-serif text-[#8E1226]">{c.value}</div>
                      <div className="mt-1 text-xs uppercase tracking-[0.18em] text-[#6D726A]">
                        {c.label}
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-14 mb-4 flex items-center justify-between flex-wrap gap-4">
                  <h2 className="text-xs uppercase tracking-[0.24em] text-[#0E6B3A]">
                    All sign-ups
                  </h2>
                  <div className="flex items-center gap-3">
                    <AddVolunteerDialog token={token} onAdded={load} />
                    <button
                      data-testid="export-csv-btn"
                      onClick={downloadCsv}
                      disabled={!(data?.signups || []).length}
                      className="inline-flex items-center gap-2 rounded-full border border-[#8E1226]/20 px-5 py-2.5 text-sm text-[#8E1226] hover:border-[#0E6B3A] hover:text-[#0E6B3A] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      <Download className="w-4 h-4" strokeWidth={1.7} />
                      Export CSV
                    </button>
                  </div>
                </div>
                <div className="rounded-2xl border border-[#8E1226]/12 overflow-hidden bg-[#FBF7F0]">
                  <Table data-testid="signups-table">
                    <TableHeader>
                      <TableRow className="border-[#8E1226]/10 hover:bg-transparent">
                        <TableHead className="text-[#6D726A]">Name</TableHead>
                        <TableHead className="text-[#6D726A]">Contact</TableHead>
                        <TableHead className="text-[#6D726A]">Day</TableHead>
                        <TableHead className="text-[#6D726A]">Role</TableHead>
                        <TableHead className="text-[#6D726A]">Time</TableHead>
                        <TableHead className="text-[#6D726A]">Notes</TableHead>
                        <TableHead className="text-[#6D726A] text-right">Remove from shift</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(data?.signups || []).length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={7} className="text-center text-[#6D726A] py-12">
                            No sign-ups yet.
                          </TableCell>
                        </TableRow>
                      ) : (
                        data.signups.map((s) => (
                          <TableRow
                            key={s.id}
                            data-testid={`signup-row-${s.id}`}
                            className="border-[#8E1226]/8"
                          >
                            <TableCell className="font-medium text-[#8E1226]">{s.name}</TableCell>
                            <TableCell className="text-[#6D726A] text-sm">
                              <div>{s.email}</div>
                              <div>{s.phone}</div>
                            </TableCell>
                            <TableCell className="text-[#8E1226]">{s.day_label}</TableCell>
                            <TableCell className="text-[#8E1226]">{s.role}</TableCell>
                            <TableCell className="text-[#6D726A] whitespace-nowrap">
                              {s.start} – {s.end}
                            </TableCell>
                            <TableCell className="text-[#6D726A] text-sm max-w-[16rem]">
                              {s.notes || "—"}
                            </TableCell>
                            <TableCell className="text-right">
                              <RemoveSignupButton signup={s} token={token} onRemoved={load} />
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
              </>
            )}
          </div>
        )}

        {tab === "reports" && (
          <div className="mt-10">
            <ReportsPanel token={token} />
          </div>
        )}

        {tab === "shifts" && (
          <div className="mt-10">
            <ShiftsPanel token={token} />
          </div>
        )}
        {tab === "content" && (
          <div className="mt-10">
            <ContentPanel token={token} />
          </div>
        )}
      </div>
    </div>
  );
}

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
  };

  if (!token) return <LoginView onLogin={setToken} />;
  return <Dashboard token={token} onLogout={logout} />;
}
